import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  X, UploadCloud, Loader2, Check, FileText, Trash2, Sparkles,
} from "lucide-react";
import api, { formatApiError } from "../api";
import { Button, Card, Input, Select } from "./ui/Primitives";

/**
 * AI-powered PDF cocktail menu importer. Two steps:
 *   1) upload PDF → /api/cocktails/import-pdf returns extracted items
 *   2) user edits the list → POST /api/imports/cocktails/pdf-confirm
 */
export default function CocktailPdfImportModal({ onClose, onDone }) {
  const { t } = useTranslation();
  const [step, setStep] = useState("upload");
  const [busy, setBusy] = useState(false);
  const [filename, setFilename] = useState("");
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const fileRef = useRef();

  const onFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/cocktails/import-pdf", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFilename(data.filename || file.name);
      setItems((data.items || []).map((it, i) => ({ ...it, _idx: i, _keep: true })));
      setCategories(data.categories || []);
      if (!data.items?.length) {
        toast.error(t("imports.pdf_no_items"));
      } else {
        setStep("review");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const update = (idx, patch) => setItems((xs) => xs.map((x, i) => i === idx ? { ...x, ...patch } : x));
  const remove = (idx) => setItems((xs) => xs.filter((_, i) => i !== idx));

  // Phase 29 F5 — validation for kept rows.
  const invalidIdx = items
    .map((x, i) => (x._keep && (!x.name || !x.name.trim()) ? i : -1))
    .filter((i) => i >= 0);
  const keptCount = items.filter((x) => x._keep).length;
  const canConfirm = invalidIdx.length === 0 && keptCount > 0;

  const confirm = async () => {
    const keep = items.filter((x) => x._keep && x.name && x.name.trim());
    if (!keep.length) {
      toast.error(t("imports.pdf_no_items_to_save"));
      return;
    }
    setBusy(true);
    try {
      const body = {
        filename,
        items: keep.map((x) => ({
          name: x.name.trim(),
          selling_price: x.selling_price ? Number(x.selling_price) : null,
          category: x.category || "Signature",
          description: (x.description || "").trim(),
        })),
      };
      const { data } = await api.post("/imports/cocktails/pdf-confirm", body);
      toast.success(t("imports.pdf_created", { count: data.created }));
      onDone?.(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-bg-void/80 backdrop-blur-sm flex justify-end" onClick={onClose}>
      <div
        className="bg-bg-void border-l border-white/10 w-full max-w-4xl h-full overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="cocktail-pdf-modal"
      >
        <header className="sticky top-0 bg-bg-void border-b border-white/10 px-6 py-4 z-10 flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-brand flex items-center gap-2">
              <Sparkles size={12} /> {t("imports.pdf_kicker")}
            </div>
            <h2 className="text-lg font-semibold">{t("imports.pdf_title")}</h2>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}><X size={14} /></Button>
        </header>

        <div className="p-6 space-y-4">
          {step === "upload" && (
            <Card className="p-8 border-dashed border-2 border-white/15 flex flex-col items-center text-center gap-4" data-testid="pdf-dropzone">
              <UploadCloud size={36} className="text-brand" />
              <div>
                <h3 className="font-semibold mb-1">{t("imports.pdf_upload_title")}</h3>
                <p className="text-sm text-ink-secondary max-w-md">{t("imports.pdf_upload_hint")}</p>
              </div>
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,application/pdf"
                className="hidden"
                onChange={(e) => onFile(e.target.files?.[0])}
                data-testid="pdf-file-input"
              />
              <Button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="pdf-pick">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
                {t("imports.pdf_pick")}
              </Button>
              <p className="text-xs text-ink-muted">{t("imports.pdf_supported")}</p>
            </Card>
          )}

          {step === "review" && (
            <>
              <div className="text-sm text-ink-secondary">
                {t("imports.pdf_extracted", { count: items.length, file: filename })}
              </div>
              <Card className="overflow-hidden">
                <div className="overflow-x-auto max-h-[65vh]">
                  <table className="w-full text-sm" data-testid="pdf-rows-table">
                    <thead className="bg-white/[0.02] sticky top-0 z-10">
                      <tr className="text-left text-[10px] uppercase tracking-wider text-ink-muted">
                        <th className="px-3 py-2 w-8"></th>
                        <th className="px-3 py-2">{t("imports.col_name")}</th>
                        <th className="px-3 py-2 w-36">{t("imports.col_category")}</th>
                        <th className="px-3 py-2 w-24 text-right">{t("imports.col_price")}</th>
                        <th className="px-3 py-2">{t("imports.col_description")}</th>
                        <th className="px-3 py-2 w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((it, i) => {
                        const isInvalid = it._keep && (!it.name || !it.name.trim());
                        return (
                        <tr key={i} className={`border-t border-white/5 ${!it._keep ? "opacity-50" : ""} ${isInvalid ? "bg-err/5" : ""}`} data-testid={`pdf-row-${i}`}>
                          <td className="px-3 py-2 text-center">
                            <input
                              type="checkbox"
                              checked={it._keep}
                              onChange={(e) => update(i, { _keep: e.target.checked })}
                              data-testid={`pdf-keep-${i}`}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <Input value={it.name} onChange={(e) => update(i, { name: e.target.value })} data-testid={`pdf-name-${i}`} />
                          </td>
                          <td className="px-3 py-2">
                            <Select value={it.category} onChange={(e) => update(i, { category: e.target.value })}>
                              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                            </Select>
                          </td>
                          <td className="px-3 py-2">
                            <Input
                              type="number"
                              step="0.01"
                              className="text-right num"
                              value={it.selling_price ?? ""}
                              onChange={(e) => update(i, { selling_price: e.target.value })}
                              data-testid={`pdf-price-${i}`}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <Input
                              value={it.description || ""}
                              onChange={(e) => update(i, { description: e.target.value })}
                              data-testid={`pdf-desc-${i}`}
                            />
                          </td>
                          <td className="px-3 py-2 text-right">
                            <Button variant="ghost" size="sm" onClick={() => remove(i)}>
                              <Trash2 size={12} className="text-err" />
                            </Button>
                          </td>
                        </tr>
                      );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>

              <div className="sticky bottom-0 bg-bg-void/95 backdrop-blur border-t border-white/10 py-3 -mx-6 px-6 flex items-center justify-between">
                <span className="text-xs text-ink-muted">
                  {invalidIdx.length > 0 ? (
                    <span className="text-err" data-testid="pdf-invalid-count">
                      {invalidIdx.length} Zeile(n) ohne Name — bitte ergänzen oder entfernen.
                    </span>
                  ) : (
                    t("imports.pdf_will_create", { count: keptCount })
                  )}
                </span>
                <div className="flex gap-2">
                  <Button variant="ghost" onClick={() => { setStep("upload"); setItems([]); }}>
                    {t("common.cancel")}
                  </Button>
                  <Button onClick={confirm} disabled={busy || !canConfirm} data-testid="pdf-confirm">
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                    {t("imports.pdf_confirm")}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
